import React, { useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';
import { IAdminAccount } from '../../api/admin';
import { formatDate, formatDateTime } from '../../utils/dates';
import AccountDialog from './AccountDialog';
import AccountStatusChip, { accountSummary } from './AccountStatusChip';
import { money } from './AccountsTable';
import OverviewPanel, { PanelField as Field } from './OverviewPanel';

interface Props {
	account: IAdminAccount | undefined;
	loading?: boolean;
}

/** Account Info: stage, terms, owner and notes; Edit opens the account dialog. */
export default function AccountInfoCard({ account, loading }: Props) {
	const [editOpen, setEditOpen] = useState(false);
	const over = !!account && account.seats != null && account.seats_active > account.seats;

	return (
		<OverviewPanel
			title="Account Info"
			caption="stage · terms · notes"
			action={
				<Button size="small" startIcon={<EditIcon />} onClick={() => setEditOpen(true)} disabled={!account}>
					Edit
				</Button>
			}
		>
			{loading || !account ? (
				<Stack spacing={1.5}>
					<Skeleton height={36} />
					<Skeleton height={36} />
					<Skeleton height={120} />
				</Stack>
			) : (
				<Stack spacing={2}>
					<Field label="Stage">
						<Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
							<AccountStatusChip account={account} />
							<Typography variant="caption" color="text.secondary">
								{accountSummary(account)}
							</Typography>
						</Stack>
					</Field>
					<Field label="Organization">
						<Link
							component={RouterLink}
							to={`/admin/organizations/${account.organization_id}`}
							state={{ organizationName: account.organization_name }}
							variant="body2"
						>
							{account.organization_name}
						</Link>
					</Field>
					<Field label="Teams covered">
						{account.teams.length > 0 ? (
							<Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
								{account.teams.map((t) => (
									<Chip
										key={t.id}
										label={t.name}
										size="small"
										variant="outlined"
										clickable
										component={RouterLink}
										to={`/admin/teams/${t.id}`}
									/>
								))}
							</Stack>
						) : (
							null
						)}
					</Field>
					<Box sx={{ display: 'flex', gap: 4 }}>
						<Field label="Start date">
							{account.start_date ? <Typography variant="body2">{formatDate(account.start_date)}</Typography> : null}
						</Field>
						<Field label="End date">
							<Typography variant="body2">{account.end_date ? formatDate(account.end_date) : 'open-ended'}</Typography>
						</Field>
					</Box>
					<Box sx={{ display: 'flex', gap: 4 }}>
						<Field label="Seats">
							<Typography variant="body2" color={over ? 'error.main' : undefined}>
								{account.seats_active} active{account.seats != null ? ` of ${account.seats}` : ' · no limit'}
							</Typography>
						</Field>
						<Field label="Value">
							<Typography variant="body2">
								{account.value != null && account.value > 0 ? `${money(account.value)} · Paid` : 'Free'}
							</Typography>
						</Field>
					</Box>
					<Field label="Owner">
						{account.owner_name || account.owner_email ? (
							<Typography variant="body2">{account.owner_name ?? account.owner_email}</Typography>
						) : (
							null
						)}
					</Field>
					<Field label="Notes">
						{account.notes ? (
							<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
								{account.notes}
							</Typography>
						) : (
							null
						)}
					</Field>
					<Typography variant="caption" color="text.secondary">
						Created {formatDate(account.created_at)}
						{account.created_by_email ? ` by ${account.created_by_email}` : ''} · updated{' '}
						{formatDateTime(account.updated_at)}
					</Typography>
				</Stack>
			)}
			<AccountDialog open={editOpen} onClose={() => setEditOpen(false)} account={account ?? null} />
		</OverviewPanel>
	);
}
